import validatejs from 'validate.js';
import { CLASS_IS_SHOW } from '../base';
import { FormDataToJSON } from '../../utils/convert';
import { getAppDefaultMessage } from '../dictionary';
import Loader from '../../utils/loader';

// Checkbox group validator
validatejs.validators.group = function(
    value: string | null,
    options: { message: string; names: string[]; count: number; form: HTMLFormElement }
) {
    let groupChecked = 0;
    options.names.map((name: string) => {
        if (options.form[name].checked) {
            groupChecked++;
        }
    });
    return groupChecked < (options.count || 1)
        ? options.message || 'z'
        : null;
    // message should be always defined, z is only for false validation just in case
};

export default abstract class Form {
    static classes = {
        error: 'error',
        success: 'success',
        sameName: 'data-samenameid'
    };
    static attributes = {
        linkedChecks: 'data-linked'
    };
    static selectors = {
        error: '[data-js=error]',
        submitBtns: 'button[type=submit]',
        arrayInput: '[data-inarray]',
        linkedChecks: `[${Form.attributes.linkedChecks}]`
    };
    readonly wrapper: HTMLFormElement;
    readonly action: string;
    readonly id: string;
    readonly destroyCallback: () => void;
    constrains: any; // https://validatejs.org/ // should be constrainTs...
    errorFlags: {
        submit: boolean;
        touched: boolean;
    };
    validatejs: validatejs.ValidateJS;
    loaders: Loader[] = [];
    formElements: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
    submitBtns: NodeListOf<HTMLButtonElement>;
    serverMessage: HTMLElement;
    typMessage: HTMLElement;

    dynamicNames: { [key: string]: string } = {};

    // this should be defined in every form
    abstract initValidation(): void;
    abstract additionalMethods(): void;

    constructor(wrapper: HTMLFormElement, options: FormOptions) {
        this.validatejs = validatejs;

        if (this.validatejs.isDomElement(wrapper)) {
            this.wrapper = wrapper;
        } else {
            throw { name: '', message: 'Wrapper powinien być istniejącym elementem DOM' };
        }

        this.action = options.action || this.wrapper.getAttribute('action') || '';
        this.id = options.id;

        if (options.constrains) {
            this.constrains = options.constrains;
        } else {
            const constraintsTpl = this.wrapper.querySelector('[data-constrains]');
            if (constraintsTpl) {
                try {
                    this.constrains = JSON.parse(
                        constraintsTpl.innerHTML
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                    );
                } catch (e) {
                    console.error(
                        `Formularz "${this.id}" nie posiada reguł walidacyjnych. Zostaną załadowane reguły po stronie front-end.`
                    );
                }
            }
        }

        this.errorFlags = {
            submit: false,
            touched: false
        };

        this.destroyCallback = options.destroyCallback;

        // typ container, when available form will be removed after successful submit
        this.typMessage = options.typField;

        //this.defaultMessages = messages.default;
        //this.customMessages = messages[id] || {};
    }

    init() {
        // submit buttons
        this.submitBtns = this.wrapper.querySelectorAll(Form.selectors.submitBtns);
        this.submitBtns.forEach(submitBtn => {
            this.loaders.push(new Loader('button'));
        });

        // gather form elements for later usage
        this.formElements = this.wrapper.querySelectorAll('input, textarea, select');
        // response container
        this.serverMessage = this.wrapper.querySelector(`[data-form${this.id}-response]`);
        this.initValidation(); // in here constraints should be defined
        this.initValidationEvents();
        this.additionalMethods();
    }

    // validation setup

    initValidationEvents() {
        this.parseConstraints();

        const inputElements = this.wrapper.querySelectorAll(
            'input[type=text], input[type=password], input[type=email], input[type=number], input[type=range], input[type=search], input[type=tel], input[type=file], input[type=datetime], input[type=date], select, textarea'
        );
        const checkElements = this.wrapper.querySelectorAll(
            'input[type=checkbox], input[type=radio]'
        );

        const _textTypes = ['text', 'email', 'search', 'tel'];

        inputElements.forEach((element: Element) => {
            element.addEventListener('input', e => {
                const _target = <HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                    e.target
                );
                if (_target) {
                    if (this.errorFlags.submit /*||  this.errorFlags[e.target.name]*/) {
                        let _errors = this.validate();
                        this.showErrorsForElement(
                            _target,
                            _errors && _errors[this.parseDOMElementName(_target.name)]
                        );
                    }
                }
            });
            element.addEventListener('blur', e => {
                const _target = <HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                    e.target
                );
                if (_target) {
                    if (_textTypes.indexOf(_target.type) > -1) {
                        _target.value = _target.value.trim();
                    }
                    // this.errorFlags[_target.name] = true;
                    let _errors = this.validate();
                    this.showErrorsForElement(
                        _target,
                        _errors && _errors[this.parseDOMElementName(_target.name)]
                    );
                }
            });
        });

        checkElements.forEach(element => {
            element.addEventListener('change', e => {
                const _target = <HTMLInputElement>e.target;
                if (_target) {
                    this.linkedConsents(_target);
                    let _errors = this.validate();
                    this.showErrorsForElement(
                        _target,
                        _errors && _errors[this.parseDOMElementName(_target.name)]
                    );
                }
            });
        });

        this.wrapper.addEventListener('submit', e => {
            e.preventDefault();
            this.errorFlags.submit = true;
            this.submit(e);
        });
    }


    /**
     * Check linked consents
     */
    linkedConsents(target: HTMLInputElement) {
        const checks = this.wrapper.querySelectorAll(
            `input[type=checkbox]${Form.selectors.linkedChecks}`
        ) as NodeListOf<HTMLInputElement>;

        checks.forEach((ch, i) => {
            const linkedCheckboxes: HTMLInputElement[] = [];
            const linkedCheckboxesNames: string[] = ch.getAttribute(Form.attributes.linkedChecks).split(',');

            for (const name in linkedCheckboxesNames) {
                linkedCheckboxes.push(this.wrapper[linkedCheckboxesNames[name]]);
            }

            if (target === ch || linkedCheckboxes.includes(target)) {
                const checkedValues = [...linkedCheckboxes]
                    .filter(input => input.checked)
                    .map(input => input.name);

                if (target === ch) {
                    linkedCheckboxes.forEach((chk, i) => {
                        chk.checked = ch.checked;
                    });
                } else {
                    if (checkedValues.length === linkedCheckboxes.length) {
                        ch.checked = true;
                    } else {
                        ch.checked = false;
                    }
                }
            }
        });
    }

    /**
     * Check specific validations and update constraints
     */
    parseConstraints() {
        // checkboxes sent as an array (Kentico specific, should handle duplicate field names)
        const checks = this.wrapper.querySelectorAll(
            `input[type=checkbox]${Form.selectors.arrayInput}`
        ) as NodeListOf<HTMLInputElement>;
        const sameNameGroups: any = {};

        // collect all groups
        checks.forEach((ch, i) => {
            if (!sameNameGroups[ch.name]) {
                sameNameGroups[ch.name] = [ch];
            } else {
                // second one with same name
                sameNameGroups[ch.name].push(ch);
            }
        });
        // iterate throught groups and update constraints if needed
        for (const name in sameNameGroups) {
            const arr = sameNameGroups[name];
            const groupNamesForValidation: string[] = [];
            //
            arr.map((checkbox: HTMLInputElement, index: number, checkGroup: []) => {
                // now update constraints for required inputs
                const name = checkbox.name;
                // pick new unique name and save old one in the attribute by the checkbox
                const chId: string = name + checkbox.value + (Math.random() * 100000).toFixed();
                checkbox.setAttribute('name', chId);
                // save name for submit
                this.dynamicNames[chId] = name;
                // modify only if there is presence attr in the constraints (required)
                if (
                    this.constrains[name] &&
                    (this.constrains[name].presence || this.constrains[name].group)
                ) {
                    // required attr by the checkbox is also required
                    if (checkbox.hasAttribute('required')) {
                        // add constraints into dynamic check for validation
                        this.constrains[chId] = { ...this.constrains[name] };
                        // if it's group need all group names
                        if (this.constrains[chId].group) {
                            // add dynamic name to validate options
                            groupNamesForValidation.push(chId);
                            this.constrains[chId].group.names = groupNamesForValidation;
                            this.constrains[chId].group.form = this.wrapper;
                        }
                    }
                    // remove the whole group after last one
                    if (index == checkGroup.length - 1) {
                        delete this.constrains[name];
                    }
                }
            });
        }
    }

    validate() {
        this.clearServerMessage();

        return this.validatejs(this.wrapper, this.constrains, { fullMessages: false }) || {};
    }

    /**
     * Gets token from current form
     * @return {String}
     */
    getToken() {
        const tokenInput = this.wrapper.querySelector(
            'input[name=__RequestVerificationToken]'
        ) as HTMLInputElement;
        if (tokenInput) {
            return tokenInput.value;
        }
        return '';
    }

    getMessage(path: string) {
        return getAppDefaultMessage(path);
    }

    // errors manage

    showErrors(errors: any) {
        let isFocused = false;
        this.formElements.forEach(element => {
            if (element.type !== 'hidden') {
                const _errors = errors && errors[this.parseDOMElementName(element.name)];

                // Since the errors can be null if no errors were found we need to handle that
                this.showErrorsForElement(element, _errors);

                // need focus on first error
                if (_errors && !isFocused) {
                    isFocused = true;
                    if (element.type === 'radio' || element.type === 'checkbox') {
                        element.scrollIntoView({ block: 'center' });
                    } else {
                        element.focus();
                    }
                }
            }
        });
    }

    showErrorsForElement(
        element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
        errors: string[]
    ) {
        let formGroup = element.parentNode as HTMLElement;
        if (formGroup) {
            if (element.type === 'checkbox' || element.type === 'radio') {
                formGroup = formGroup.parentNode as HTMLElement;
            }
            this.resetFormElement(formGroup);
            if (errors) {
                const placeError = formGroup.querySelector(Form.selectors.error);
                if (placeError) {
                    // placeError.classList.add(Form.classes.error);
                    errors.forEach(textErrors => {
                        placeError.textContent += `${textErrors} `;
                    });
                }

                formGroup.classList.add(Form.classes.error);
            } else {
                formGroup.classList.add(Form.classes.success);
            }
        }
    }

    resetFormElement(element: HTMLElement | null) {
        if (element) {
            // Remove the success and error classes
            element.classList.remove(Form.classes.error);
            element.classList.remove(Form.classes.success);
            // remove any old messages
            const placeError = element.querySelector(Form.selectors.error);
            if (placeError) {
                placeError.innerHTML = '';
            }
        }
    }

    clearServerMessage() {
        if (this.serverMessage) {
            const parent = this.serverMessage.parentNode as HTMLElement;
            if (parent) {
                parent.classList.remove(CLASS_IS_SHOW);
            }
            this.serverMessage.innerHTML = '';
        }
    }

    // submit

    submit(e: Event) {
        let _errors = this.validate();
        console.log('%cFORM trying submit, errors: ' + JSON.stringify(_errors), 'color:grey');

        this.showErrors(_errors || {});

        this.resolveSubmitErrors(_errors);
    }

    resolveSubmitErrors(errors: any) {
        if (Object.keys(errors).length == 0) {
            this.setupSend();
        } else {
            // errors manage if needed
        }
    }

    setupSend() {
        this.showLoader();
        this.clearServerMessage();

        const formData = new FormData(this.wrapper);
        // convert into object
        const data = FormDataToJSON(formData, this.dynamicNames);
        // console.log(data);
        this.send(JSON.stringify(data));
    }

    send(formData: string) {
        const token = this.getToken();
        const headers = {
            'Content-Type': 'application/json',
            __RequestVerificationToken: token
        };
        fetch(this.action, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            headers: headers
        })
            .then(res => {
                if (res.status >= 200 && res.status < 500 /*response.ok*/) {
                    return res.json();
                }
                throw '';
            })
            .then(this.successCallback.bind(this))
            .catch(this.errorCallback.bind(this));
    }

    /**
     * Promise complete
     * @param response - response json
     */
    successCallback(response: StandardResponse) {
        if (response.Status) {
            // TYP
            this.successManage(response);
        } else {
            this.errorCallback(response);
        }
    }

    /**
     * Default
     * @param response - response json
     */
    successManage(response: StandardResponse) {
        if (this.typMessage) {
            // remove form and show typ container provided with options
            this.typMessage.classList.add(CLASS_IS_SHOW);
            this.wrapper.parentNode.removeChild(this.wrapper);
        } else if (this.serverMessage) {
            // just reset form and and show message
            const parent = this.serverMessage.parentNode as HTMLElement;

            if (response.Message) {
                this.serverMessage.textContent = response.Message;
            } else {
                this.serverMessage.textContent = this.getMessage('success');
            }

            if (parent) {
                parent.classList.add(CLASS_IS_SHOW);
            }
            this.reset();
            this.hideLoader();
        } else {
            this.hideLoader();
        }
    }

    errorCallback(response: StandardResponse) {
        if (response && response.Message) {
            this.errorManage(response.Message);
        } else {
            this.errorManage(this.getMessage('errors.default'));
        }
    }

    errorManage(message: string, container?: HTMLElement) {
        if (container) {
            container.innerHTML = '<span>' + message + '</span>';
        } else if (this.serverMessage) {
            const parent = this.serverMessage.parentNode as HTMLElement;
            if (parent) {
                parent.classList.add(CLASS_IS_SHOW);
            }

            this.serverMessage.innerHTML = message;
        }

        this.hideLoader();
    }

    reset() {
        this.formElements.forEach(element => {
            let _parent = element.parentNode as HTMLElement;
            if (_parent) {
                if (element.type === 'checkbox' || element.type === 'radio') {
                    _parent = _parent.parentNode as HTMLElement;
                    element = element as HTMLInputElement;
                    element.checked = false;
                } else if (element) {
                    element.value = '';
                }
                _parent.classList.remove(Form.classes.success);
                _parent.classList.remove(Form.classes.error);
            }
        });

        this.hideLoader();
    }

    // loading manage

    showLoader() {
        this.submitBtns.forEach((submitBtn, index) => {
            submitBtn.disabled = true;
            this.loaders[index].show(submitBtn, true);
        });
    }

    hideLoader() {
        this.submitBtns.forEach((submitBtn, index) => {
            submitBtn.disabled = false;
            this.loaders[index].hide();
        });
    }

    parseDOMElementName(name: string) {
        return name.replace('.', '\\.');
    }
}
